import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { initialState, setting } from '@/Helpers.js';
import store from '@/store/Index.js';

// Initialization promise for waiting on Amplitude to be ready
let amplitudeInitPromise = null;
let amplitudeInitResolver = null;

/**
 * Wait for Amplitude SDK to initialize with a timeout
 * @param {number} timeoutMs - Maximum time to wait in milliseconds
 * @returns {Promise<{initialized?: boolean, timedOut?: boolean}>}
 */
export const waitForAmplitudeInit = (timeoutMs = 5000) => {
    if (!amplitudeInitPromise) {
        return Promise.resolve({ initialized: false });
    }

    return Promise.race([
        amplitudeInitPromise.then(() => ({ initialized: true })),
        new Promise(resolve => setTimeout(() => resolve({ timedOut: true }), timeoutMs)),
    ]);
};

/**
 * Check if survey tracking is enabled via environment setting
 * @returns {boolean}
 */
const isSurveyTrackingEnabled = () => {
    const userSetting = initialState(`UserSettings.amplitude_survey_tracking_enabled`, null);
    const institutionSetting = initialState(`InstitutionSettings.amplitude_survey_tracking_enabled`, null);
    const envSetting = initialState(`EnvironmentSettings.amplitude_survey_tracking_enabled`, null);
    const finalValue = setting('amplitude_survey_tracking_enabled', false);

    console.debug('[Amplitude] Survey tracking setting check:', {
        userSetting,
        institutionSetting,
        envSetting,
        finalValue,
        enabled: Boolean(finalValue),
    });

    return Boolean(finalValue);
};

/**
 * Check if Amplitude is initialized and ready for tracking
 * @returns {boolean}
 */
const isAmplitudeInitialized = () => {
    try {
        const deviceId = amplitude.getDeviceId();
        return Boolean(deviceId);
    } catch (_error) {
        // Expected when Amplitude hasn't initialized yet
        return false;
    }
};

/**
 * Track when an automated survey is started
 * Used for Targeted Replay Capture (TRC) in Amplitude
 * @param {Object} assignment - The automated survey assignment
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAutomatedSurveyStarted = async assignment => {
    if (!isSurveyTrackingEnabled()) {
        console.debug('[Amplitude] Survey tracking disabled - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'disabled' };
    }

    // Wait for Amplitude to initialize (with 5 second timeout)
    const initResult = await waitForAmplitudeInit(5000);

    if (initResult.timedOut) {
        console.warn('[Amplitude] Initialization timeout - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'timeout' };
    }

    if (!initResult.initialized) {
        console.warn('[Amplitude] SDK not initialized - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'not_initialized' };
    }

    // Double-check initialization state
    if (!isAmplitudeInitialized()) {
        console.warn('[Amplitude] SDK not ready - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'not_ready' };
    }

    const eventProperties = {
        assignment_id: assignment.id,
        survey_id: assignment.survey_id,
        is_evaluation: Boolean(assignment.course_id),
        is_anonymous: Boolean(assignment.is_anonymous),
        is_locked: Boolean(assignment.is_locked_now || assignment.is_locked),
        time_limit: assignment.time_limit ?? null,
        course_id: assignment.course_id ?? null,
    };

    try {
        const deviceId = amplitude.getDeviceId();
        const sessionId = amplitude.getSessionId();
        console.debug('[Amplitude] Tracking "Automated Survey Started" event:', {
            ...eventProperties,
            device_id: deviceId,
            session_id: sessionId,
        });

        amplitude.track('Automated Survey Started', eventProperties);

        // Flush to ensure event is sent before navigation
        await amplitude.flush();

        console.debug('[Amplitude] Successfully tracked and flushed "Automated Survey Started" event');
        return { tracked: true };
    } catch (error) {
        console.error('[Amplitude] Failed to track survey started:', error);
        return { skipped: true, reason: 'error' };
    }
};

/**
 * Track when an automated survey ends
 * Used for Targeted Replay Capture (TRC) in Amplitude
 * @param {Object} options - Survey end details
 * @param {number} options.assignmentId - The assignment ID
 * @param {number} options.surveyId - The survey ID
 * @param {boolean} options.isEvaluation - Whether this is a course evaluation
 * @param {string} options.outcome - The outcome (completed, expired, abandoned)
 * @param {number} [options.durationSeconds] - How long the survey took
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAutomatedSurveyEnded = async ({ assignmentId, surveyId, isEvaluation, outcome, durationSeconds }) => {
    if (!isSurveyTrackingEnabled()) {
        console.debug('[Amplitude] Survey tracking disabled - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'disabled' };
    }

    // Wait for Amplitude to initialize (with 5 second timeout)
    const initResult = await waitForAmplitudeInit(5000);

    if (initResult.timedOut) {
        console.warn('[Amplitude] Initialization timeout - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'timeout' };
    }

    if (!initResult.initialized) {
        console.warn('[Amplitude] SDK not initialized - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'not_initialized' };
    }

    // Double-check initialization state
    if (!isAmplitudeInitialized()) {
        console.warn('[Amplitude] SDK not ready - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'not_ready' };
    }

    const eventProperties = {
        assignment_id: assignmentId,
        survey_id: surveyId,
        is_evaluation: Boolean(isEvaluation),
        outcome: outcome || 'completed',
        duration_seconds: durationSeconds ?? null,
    };

    try {
        const deviceId = amplitude.getDeviceId();
        const sessionId = amplitude.getSessionId();
        console.debug('[Amplitude] Tracking "Automated Survey Ended" event:', {
            ...eventProperties,
            device_id: deviceId,
            session_id: sessionId,
        });

        amplitude.track('Automated Survey Ended', eventProperties);

        // Flush to ensure event is sent before navigation
        await amplitude.flush();

        console.debug('[Amplitude] Successfully tracked and flushed "Automated Survey Ended" event');
        return { tracked: true };
    } catch (error) {
        console.error('[Amplitude] Failed to track survey ended:', error);
        return { skipped: true, reason: 'error' };
    }
};

export default {
    install(app) {
        // Create the initialization promise before starting
        amplitudeInitPromise = new Promise(resolve => {
            amplitudeInitResolver = resolve;
        });

        // Initialize Amplitude only if the feature is enabled and we have a user
        const initAmplitude = async () => {
            const { user } = store.state.Auth;
            const featureClass = store.state?.features?.AMPLITUDE;

            if (!user) {
                // No user yet - don't resolve promise, wait for user to load
                console.debug('[Amplitude] No user yet - waiting for user to load');
                return;
            }

            if (!user.hasActiveFeature(featureClass)) {
                // Feature disabled - resolve promise so tracking doesn't block forever
                amplitudeInitResolver?.();
                console.debug('[Amplitude] Feature disabled - initialization skipped');
                return;
            }

            const apiKey = setting('amplitude_api_key');
            const instanceId = setting('amplitude_instance_id');

            if (!apiKey || !instanceId) {
                console.error('[Amplitude] Missing API key or instance ID');
                amplitudeInitResolver?.();
                return;
            }

            const sessionReplayTracking = sessionReplayPlugin({
                blockClass: 'is-amplitude-block',
                maskTextClass: 'is-amplitude-mask',
                replaysSessionSampleRate: 1.0,
                replaysOnErrorSampleRate: 1.0,
                maskAllInputs: true,
                maskAllText: false,
                forceSessionTracking: true,
                recordCanvas: true,
                recordCrossOriginIframes: true,
            });

            amplitude.add(sessionReplayTracking);

            amplitude.add(engagementPlugin());

            await amplitude.init(apiKey, {
                serverZone: 'US',
                defaultTracking: {
                    sessions: true,
                    pageViews: true,
                    formInteractions: true,
                    fileDownloads: true,
                },
                autocapture: true,
                transport: 'xhr',
                useBeacon: false,
                serverUrl: 'https://api2.amplitude.com/2/httpapi',
                minIdLength: 1,
                trackingOptions: {
                    ipAddress: true,
                    language: true,
                    platform: true,
                    region: true,
                    version: true,
                },
            });

            amplitude.setUserId(user.id.toString());

            const identifyObj = new amplitude.Identify()
                .set('tenant_id', instanceId)
                .set('user_id', user.id.toString());

            amplitude.identify(identifyObj);

            // Log initialization details for debugging
            const deviceId = amplitude.getDeviceId();
            const sessionId = amplitude.getSessionId();
            console.debug('[Amplitude] Initialized successfully:', {
                device_id: deviceId,
                session_id: sessionId,
                user_id: user.id.toString(),
                session_replay_enabled: true,
            });

            // Resolve the initialization promise
            amplitudeInitResolver?.();

            // Expose amplitude instance and survey tracking methods
            app.config.globalProperties.$amplitude = amplitude;
            app.config.globalProperties.$trackAutomatedSurveyStarted = trackAutomatedSurveyStarted;
            app.config.globalProperties.$trackAutomatedSurveyEnded = trackAutomatedSurveyEnded;

            const waitForEngagement = (timeout = 5000, interval = 50) => {
                return new Promise((resolve, reject) => {
                    if (typeof window === 'undefined') {
                        reject(new Error('Engagement plugin unavailable (SSR)'));
                        return;
                    }

                    const startTime = Date.now();

                    const check = () => {
                        if (
                            typeof window !== 'undefined' &&
                            typeof window.engagement !== 'undefined' &&
                            window.engagement.init
                        ) {
                            resolve();
                        } else if (Date.now() - startTime >= timeout) {
                            reject(new Error('Engagement plugin failed to load within timeout'));
                        } else {
                            setTimeout(check, interval);
                        }
                    };

                    check();
                });
            };

            try {
                await waitForEngagement();
                await window.engagement.init(apiKey);
                window.engagement.boot();
            } catch (error) {
                console.error('[Amplitude] Failed to initialize engagement browser:', error);
            }
        };

        // Watch for user changes in the store

        store.watch(
            state => state.Auth.user,
            newUser => {
                if (newUser) {
                    initAmplitude();
                }
            },
            { immediate: true }
        );
    },
};
